import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import * as validation from '../../lib/validation';
import {withUser} from '../../lib/user';
import {toMap} from '../../lib/helpers';
import {buildCatalogTree, buildNameListFromTree} from '../../lib/catalog';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Card from '../../components/Card';
import Button from '../../components/Button';
import JsonLogicInput from '../../components/JsonLogicInput';
import TemplateWidget from '../../components/TemplateWidget';
import Placeholder from '../../components/Placeholder';
import {getQuestionDisplayLabel} from '../FormBuilderScreen/shared';

class WidgetFormModal extends PureComponent {
  state = this.getInitialState();

  getInitialState() {
    const widget = this.props.widget;

    return {
      saving: false,
      validationEnabled: false,
      previewHasError: false,
      previewKey: 1,
      ...widget,
      type: getWidgetType(widget)
    };
  }

  componentDidUpdate(prevProps) {
    const {widget} = this.props;

    if (prevProps.widget !== widget) {
      this.setState(this.getInitialState());
    }
  }

  handleChangeType = type => {
    this.setState(state => {
      const previewKey = state.previewKey;

      if (state.type === type) {
        // Type was not changed. Do nothing.
        return null;
      }

      // A submissions widget's formula and question are written by its own picker, and neither survives the move to
      // a chart: the formula is a bare reference the formula builder has no variable for, and the question is one the
      // grouping picker may not offer.
      const leavingSubmissions = state.type === 'submissions';
      const map = leavingSubmissions ? null : state.map;
      const groupByQuestion = leavingSubmissions ? null : state.groupByQuestion;

      if (type === 'line') {
        return {type: 'line', aggregation: null, groupByQuestion: null, map, previewKey};
      }

      if (type === 'summary') {
        return {type: 'summary', groupByQuestion: null, map, previewKey};
      }

      if (type === 'submissions') {
        // A submissions widget lists answers rather than combining them, so it has no aggregation. Its formula is
        // written by the question picker below.
        return {type, aggregation: null, groupByQuestion: null, map: null, previewKey};
      }

      return {type, map, groupByQuestion, previewKey};
    });
  }

  handleChangeSubmissionsQuestion = questionId => {
    // Every widget the API stores has to have a formula, so a submissions widget's is the answer it lists.
    this.setFormState({
      groupByQuestion: questionId,
      aggregation: null,
      map: questionId ? JSON.stringify({var: questionId}) : null
    });
  }

  handlePreviewError = () => {
    this.setState({previewHasError: true});
  }

  setFormState = nextState => {
    this.setState(state => ({
      ...nextState,
      previewKey: state.previewHasError ? state.previewKey + 1 : state.previewKey,
      previewHasError: false
    }));
  }

  handleSaveWidget = async () => {
    const {template, credentials, onSave} = this.props;
    // The API has no type of its own to be given: it works the widget's kind out from the aggregation and the
    // grouping question, and sends the name it arrived at back down. So only the fields it stores are sent.
    let {saving, validationEnabled, previewHasError, previewKey, type, ...widget} = this.state;
    const errors = getValidationErrors(widget, type);
    const hasErrors = hasValidationErrors(errors);

    if (hasErrors) {
      return this.setState({validationEnabled: true});
    }

    this.setState({saving: true});

    try {
      if (widget.isNewWidget) {
        widget = await api.createWidget(template.id, widget, credentials);
      }
      else {
        widget = await api.editWidget(template.id, widget, credentials);
      }

      NotificationManager.success('Your widget was saved!');
      onSave(widget);
    }
    catch (e) {
      this.setState({saving: false});
      // The API helpers throw the response itself, so read what it objected to rather than losing it.
      NotificationManager.error('An error occurred while saving your widget. Please try again.');
    }
  }

  render() {
    const {open, onClose, template, catalog, sheets, rows, user} = this.props;
    const {saving, validationEnabled, previewHasError, type, previewKey, ...widget} = this.state;
    const nameOptions = buildNameListFromTree('', '', buildCatalogTree(catalog)).map(item => ({name: item.name, value: item.id}));
    const allQuestions = template.contextQuestions.concat(template.questions);
    const questions = allQuestions
      .filter(question => question.type !== 'IMAGE' && question.type !== 'SIGNATURE')
      .map(question => {
        if (question.type === 'CATALOG_RADIO' || question.type === 'CATALOG_CHECKBOX') {
          question.questionOptions = nameOptions;
        }
        return question;
      });
    const filterVariables = questions.reduce(toVariableMap, {});
    const mappingVariables = questions.filter(question => question.type === 'NUMERIC' || question.type === 'MEASURED_NUMERIC').reduce(toVariableMap, {});
    const groupingQuestions = questions.filter(isValidGroupingQuestion);
    // A submissions widget just lists the answer, so every question type works. Context questions are answered once
    // per sheet rather than once per submission, so they are left out.
    const submissionQuestions = template.questions;
    const errors = getValidationErrors(widget, type, {disabled: !validationEnabled});
    const hasErrors = hasValidationErrors(errors);

    return (
      <Form onSubmit={this.handleSaveWidget}>
        <SideModal
          large
          open={open}
          title={widget && widget.id ? 'Edit Widget' : 'Create Widget'}
          onClose={onClose}
          options={[
            <Button type="submit" disabled={saving || hasErrors}>{saving ? 'Saving...' : 'Save Widget'}</Button>,
            <Button secondary onClick={onClose}>Cancel</Button>
          ]}>
          <WidgetFormContainer>
            <WidgetPreviewContainer>
              {widget.map ? (
                <TemplateWidget
                  preview
                  id={'widget-preview'}
                  key={previewKey}
                  sheetsById={toMap(sheets, sheet => sheet.id)}
                  rows={rows}
                  widget={widget}
                  catalog={catalog}
                  questions={toMap(allQuestions, question => question.id)}
                  onError={this.handlePreviewError}
                  user={user}
                />
              ) : (
                <NoPreview />
              )}
            </WidgetPreviewContainer>
            <WidgetForm>
              <WidgetTypes>
                <WidgetType
                  active={type === 'line'}
                  title="Line Chart" 
                  icon={<Icon name="trending-up" size="2em" />}
                  onClick={() => this.handleChangeType('line')}
                />
                <WidgetType
                  active={type === 'summary'}
                  title="Data Summary"
                  icon={<Icon name="monitor" size="2em" />}
                  onClick={() => this.handleChangeType('summary')}
                />
                <WidgetType
                  active={type === 'bar'}
                  title="Bar Chart" 
                  icon={<Icon name="bar-chart-2" size="2em" />}
                  onClick={() => this.handleChangeType('bar')}
                />
                <WidgetType
                  active={type === 'submissions'}
                  title="Recent Submissions"
                  icon={<Icon name="list" size="2em" />}
                  onClick={() => this.handleChangeType('submissions')}
                />
              </WidgetTypes>
              <Form.Group>
                <Input
                  key={Math.random()}
                  type="text"
                  id="widget-name"
                  label="Widget Name"
                  defaultValue={widget.title || ''}
                  onBlur={e => this.setFormState({title: e.target.value})}
                  invalid={errors.title}
                  validationText={errors.title}
                />
              </Form.Group>
              {type === 'submissions' && (
                <Form.Group>
                  <Input
                    type="select"
                    id="widget-submissions-question"
                    label="Question"
                    value={widget.groupByQuestion || ''}
                    onChange={e => this.handleChangeSubmissionsQuestion(!e.target.value ? null : e.target.value)}
                    helpText="Every submission that answered this question is listed, newest first"
                    invalid={errors.groupByQuestion}
                    validationText={errors.groupByQuestion}>
                    <option value="">Choose a question...</option>
                    {submissionQuestions.map(question => (
                      <option key={question.id} value={question.id}>{getQuestionDisplayLabel(question)}</option>
                    ))}
                  </Input>
                </Form.Group>
              )}
              {(type === 'line' || type === 'summary' || type === 'bar') && (
              <Form.Group>
                <JsonLogicInput
                  label="Formula"
                  variables={mappingVariables}
                  value={widget.map ? JSON.parse(widget.map) : null}
                  onChange={map => this.setFormState({map: JSON.stringify(map)})}
                  helpText="Combine data from a sheet submission into a single value using this formula"
                  invalid={errors.map}
                  validationText={errors.map}
                /> 
              </Form.Group>
              )}
              <Form.Group>
                <JsonLogicInput
                  label="Filter"
                  variables={filterVariables}
                  value={widget.filter ? JSON.parse(widget.filter) : null}
                  onChange={filter => this.setFormState({filter: JSON.stringify(filter)})}
                  helpText="Choose a condition that a submission must match to be included"
                /> 
              </Form.Group>
              {(type === 'summary' || type === 'bar') && (
                <Form.Group>
                  <Input
                    type="select"
                    id="widget-aggregation"
                    label="Aggregation"
                    value={widget.aggregation || ''}
                    onChange={e => this.setFormState({aggregation: !e.target.value ? null : e.target.value})}
                    helpText="Choose how to combine each submission into a single value"
                    invalid={errors.aggregation}
                    validationText={errors.aggregation}>
                    <option value="">Choose an aggregation...</option>
                    <option value="COUNT">Count</option>
                    <option value="SUM">Sum</option>
                    <option value="AVG">Average</option>
                    <option value="MIN">Minimum</option>
                    <option value="MAX">Maximum</option>
                  </Input>
                </Form.Group>
              )}
              {type === 'bar' && (
                <Form.Group>
                  <Input
                    type="select" 
                    id="widget-grouping" 
                    label="Group by"
                    value={widget.groupByQuestion || ''}
                    onChange={e => this.setFormState({groupByQuestion: !e.target.value ? null : e.target.value})}
                    helpText="Choose the question that provides the bars on the bar chart"
                    invalid={errors.groupByQuestion}
                    validationText={errors.groupByQuestion}>
                    <option value="">Choose a question...</option>
                    {groupingQuestions.map(question => (
                      <option key={question.id} value={question.id}>{getQuestionDisplayLabel(question)}</option>
                    ))}
                  </Input>
                </Form.Group>
              )}
            </WidgetForm>
          </WidgetFormContainer>
        </SideModal>
      </Form>
    );
  }
}

// The names the API gives each kind of widget, against the ones this form uses.
const WIDGET_TYPES_BY_API_TYPE = {
  TREND: 'line',
  SUMMARY: 'summary',
  BAR_CHART: 'bar',
  RECENT_SUBMISSIONS: 'submissions'
};

function getWidgetType(widget) {
  if (!widget) {
    return null;
  }

  if (WIDGET_TYPES_BY_API_TYPE[widget.type]) {
    return WIDGET_TYPES_BY_API_TYPE[widget.type];
  }

  // A widget that arrives without one - a new widget, or a pinned one, which the API sends down in a shorter form.
  // The rule is the API's: an aggregation combines the values, a question breaks them out.
  if (widget.aggregation) {
    return widget.groupByQuestion ? 'bar' : 'summary';
  }

  return widget.groupByQuestion ? 'submissions' : 'line';
}

function toVariableMap(map, question) {
  map[question.id] = {
    label: getQuestionDisplayLabel(question),
    type: question.type,
    options: question.questionOptions.reduce((list, option) => {
      list.push({
        name: option.name ? option.name : option.value,
        value: option.value
      });
      return list;
    },[])
  };
  return map;
}

function isValidGroupingQuestion(question) {
  switch (question.type) {
    case 'TEXT':
    case 'RADIO':
    case 'BOOLEAN':
    case 'NUMERIC':
    case 'MEASURED_NUMERIC':
    case 'DATETIME':
    case 'CATALOG_RADIO':
      return true;
    default:
      return false;
  }
}

const isEmptyValue = validation.create((value, {isEmpty, trim}) => !value || isEmpty(trim(value)));

function getValidationErrors(widget, type, {disabled = false} = {}) {
  if (disabled) {
    return {};
  }

  return {
    title: isEmptyValue(widget.title) ? 'A widget name is required' : null,
    map: type !== 'submissions' && !widget.map ? 'A formula is required' : null,
    aggregation: (type === 'summary' || type === 'bar') && isEmptyValue(widget.aggregation) ? 'An aggregation is required' : null,
    groupByQuestion: type === 'submissions'
      ? (isEmptyValue(widget.groupByQuestion) ? 'A question is required' : null)
      : (type === 'bar' && isEmptyValue(widget.groupByQuestion) ? 'A grouping value is required' : null)
  };
}

function hasValidationErrors(errors) {
  return Object.keys(errors).filter(key => Boolean(errors[key])).length > 0;
}

const NoPreview = styled(props => (
  <Card {...props}>
    <Placeholder height="40rem">
      Your widget preview will appear here.
    </Placeholder>
  </Card>
))`
  padding: 1rem;
`;

const WidgetFormContainer = styled(SideModal.Content)`
  display: flex;
`;

const WidgetPreviewContainer = styled.div`
  width: 50%;
  padding: 0 2rem 0 0;

  @media (max-width: 64rem) {
    display: none;
  }
`;

const WidgetForm = styled.div`
  width: 50%;

  @media (max-width: 64rem) {
    width: 100%;
  }
`;

const WidgetTypes = styled.div`
  display: flex;
  margin: 0 -0.5rem;
`;

const WidgetType = styled(({title, icon, onClick, ...props}) => (
  <Button blank block onClick={onClick}>
    <Card {...props}>
      {icon}
      <h4>{title}</h4>
    </Card>
  </Button>
))`
  text-align: center;
  flex: 1;
  margin: 0 0.5rem;

  h4 {
    margin: 0;
    font-weight: 400;
    padding: 0.5rem 0 1rem 0;
  }

  ${Icon} {
    margin-top: 1rem;
  }
`;

export default withUser(WidgetFormModal);